import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { fmtMonth, NOW, toAbs } from "@/lib/months";
import { fmtInput, fmtShort, parseAmount } from "@/lib/format";
import { sketchRange, tidy, type SketchPoint } from "@/lib/sketch";
import { cn } from "@/lib/utils";
import { useWidth } from "@/components/charts/chart-utils";

export type SketchStage = "range" | "dates" | "shape";

/** Left gutter (the range boxes), bottom gutter (the date boxes), and the plot height. */
const L = 120;
const R = 20;
const T = 22;
const B = 64;
const PLOT_H = 220;
/** Hit area for a handle: at least 44 px (J1). */
const HIT = 22;

/**
 * The chart in the "New metric" dialog. It starts grey. First the y-axis asks for the lowest and highest values,
 * then the x-axis asks for the dates, then a line appears with handles to drag into shape.
 */
export function MetricSketch({
	stage,
	unit,
	low,
	high,
	onLow,
	onHigh,
	start,
	end,
	onStart,
	onEnd,
	points,
	onPoints,
}: {
	stage: SketchStage;
	unit: string;
	low: string;
	high: string;
	onLow: (v: string) => void;
	onHigh: (v: string) => void;
	start: string;
	end: string;
	onStart: (m: string) => void;
	onEnd: (m: string) => void;
	points: SketchPoint[] | null;
	onPoints: (p: SketchPoint[]) => void;
}) {
	const [wrapRef, W] = useWidth<HTMLDivElement>();
	const svgRef = useRef<SVGSVGElement>(null);
	const [drag, setDrag] = useState<number | null>(null);
	const [focus, setFocus] = useState<number | null>(null);
	const H = T + PLOT_H + B;
	const iw = W - L - R;

	const lo = parseAmount(low, unit);
	const hi = parseAmount(high, unit);
	const shaping = stage === "shape" && points && lo !== null && hi !== null && hi > lo;
	const pad = shaping ? (hi! - lo!) * 0.06 : 0;
	const y = (v: number) => (shaping ? T + (1 - (v - lo! + pad) / (hi! - lo! + 2 * pad)) * PLOT_H : T + PLOT_H / 2);
	const vAt = (py: number) => lo! - pad + (1 - (py - T) / PLOT_H) * (hi! - lo! + 2 * pad);
	const a0 = toAbs(start);
	const a1 = Math.max(a0 + 1, toAbs(end));
	const x = (m: string) => L + ((toAbs(m) - a0) / (a1 - a0)) * iw;

	const pts: [number, number][] = shaping ? points!.map((p) => [x(p.month), y(p.v)]) : [];
	const band = shaping ? sketchRange(points!, lo!, hi!) : null;
	const bandPath = band
		? smooth(points!.map((p) => [x(p.month), y(band[p.month][2])])) +
			"L" +
			smooth(
				points!
					.slice()
					.reverse()
					.map((p) => [x(p.month), y(band[p.month][0])]),
			).slice(1) +
			"Z"
		: "";

	function move(i: number, v: number) {
		if (!shaping) return;
		const span = hi! - lo!;
		const next = points!.slice();
		next[i] = { ...next[i], v: tidy(Math.max(lo!, Math.min(hi!, v)), span) };
		onPoints(next);
	}

	function onPointerMove(e: React.PointerEvent) {
		if (drag === null) return;
		const r = svgRef.current!.getBoundingClientRect();
		move(drag, vAt(e.clientY - r.top));
	}

	function onKey(i: number, e: React.KeyboardEvent) {
		if (!shaping) return;
		const step = (hi! - lo!) * (e.shiftKey ? 0.1 : 0.01);
		const v = points![i].v;
		const to = e.key === "ArrowUp" ? v + step : e.key === "ArrowDown" ? v - step : e.key === "Home" ? lo! : e.key === "End" ? hi! : null;
		if (to === null) return;
		e.preventDefault();
		move(i, to);
	}

	const grid = [0, 0.25, 0.5, 0.75, 1].map((f) => T + f * PLOT_H);
	const placeholder = "M0,0 C" + [0.2, 0.35, 0.5, 0.65, 0.8, 1].map((f, i) => `${f * iw},${(i % 2 ? -1 : 1) * 18 * (1 - f / 2)}`).join(" ");

	return (
		<div ref={wrapRef} className="relative select-none" style={{ height: H }}>
			<svg
				ref={svgRef}
				width={W}
				height={H}
				className="absolute inset-0 touch-none"
				onPointerMove={onPointerMove}
				onPointerUp={() => setDrag(null)}
				onPointerCancel={() => setDrag(null)}
				role="img"
				aria-label={shaping ? `A line from ${fmtMonth(start)} to ${fmtMonth(end)} with ${points!.length} handles to drag.` : "An empty chart."}
			>
				{/* The axis boxes light up in turn. */}
				<rect
					x={4}
					y={T - 14}
					width={L - 12}
					height={PLOT_H + 28}
					rx={6}
					className={cn("transition-opacity duration-300", stage === "range" ? "opacity-100" : "opacity-0")}
					fill="var(--brand-periwinkle-wash)"
					stroke="var(--brand-periwinkle)"
				/>
				<rect
					x={L - 8}
					y={T + PLOT_H + 6}
					width={iw + 16}
					height={B - 8}
					rx={6}
					className={cn("transition-opacity duration-300", stage === "dates" ? "opacity-100" : "opacity-0")}
					fill="var(--brand-periwinkle-wash)"
					stroke="var(--brand-periwinkle)"
				/>
				{grid.map((gy, i) => (
					<line key={i} x1={L} x2={W - R} y1={gy} y2={gy} stroke="var(--grid)" strokeDasharray={i === 0 || i === 4 ? undefined : "3 4"} />
				))}
				<line x1={L} x2={L} y1={T} y2={T + PLOT_H} stroke="var(--hex-line)" />
				<line x1={L} x2={W - R} y1={T + PLOT_H} y2={T + PLOT_H} stroke="var(--hex-line)" />

				{!shaping && (
					<path
						d={placeholder}
						transform={`translate(${L},${T + PLOT_H / 2})`}
						fill="none"
						stroke="var(--hex-line)"
						strokeWidth={3}
						strokeLinecap="round"
						strokeDasharray="2 8"
					/>
				)}

				{shaping && (
					// Keyed on the dates and range, so the line draws itself again when they change.
					<g key={`${start}:${end}:${low}:${high}`}>
						<path d={bandPath} fill="var(--model)" opacity={0.1} className="sketch-fade" />
						<path d={smooth(pts)} fill="none" stroke="var(--model)" strokeWidth={2.5} strokeLinecap="round" pathLength={1} className="sketch-draw" />
						{points!.map((p, i) => {
							const active = drag === i || focus === i;
							const [px, py] = pts[i];
							const label = `${fmtShort(p.v, unit)}`;
							return (
								<g key={p.month} className="sketch-pop" style={{ animationDelay: `${700 + i * 90}ms` }}>
									<rect x={px - HIT} y={py - HIT} width={HIT * 2} height={HIT * 2} fill="transparent" />
									<circle
										cx={px}
										cy={py}
										r={active ? 10 : 8}
										fill="var(--model)"
										stroke="var(--card)"
										strokeWidth={2.5}
										className="cursor-grab outline-none focus-visible:stroke-[var(--ring)]"
										tabIndex={0}
										role="slider"
										aria-label={`Likely value for ${fmtMonth(p.month)}`}
										aria-valuemin={lo!}
										aria-valuemax={hi!}
										aria-valuenow={p.v}
										aria-valuetext={label}
										onPointerDown={(e) => {
											(e.target as Element).setPointerCapture?.(e.pointerId);
											setDrag(i);
										}}
										onKeyDown={(e) => onKey(i, e)}
										onFocus={() => setFocus(i)}
										onBlur={() => setFocus(null)}
										style={{ transition: drag === i ? undefined : "r 120ms" }}
									/>
									<text
										x={px}
										y={py - 16}
										textAnchor="middle"
										className={cn("fill-foreground text-[11px] font-medium tabular-nums transition-opacity", active ? "opacity-100" : "opacity-0")}
									>
										{label}
									</text>
								</g>
							);
						})}
					</g>
				)}
			</svg>

			{/* The range boxes, on the y-axis. */}
			<AxisBox
				style={{ left: 12, top: T - 8, width: L - 28 }}
				label="Highest"
				live={stage === "range"}
				value={high}
				onChange={onHigh}
				unit={unit}
				placeholder={unit === "USD" ? "$10M" : unit === "%" ? "40%" : "100"}
				autoFocus={stage === "range"}
				readOnly={stage === "shape"}
			/>
			<AxisBox
				style={{ left: 12, top: T + PLOT_H - 46, width: L - 28 }}
				label="Lowest"
				below
				live={stage === "range"}
				value={low}
				onChange={onLow}
				unit={unit}
				placeholder={unit === "USD" ? "$2M" : unit === "%" ? "5%" : "0"}
				readOnly={stage === "shape"}
			/>

			{/* The date boxes, on the x-axis. */}
			<div
				className={cn("absolute flex items-end justify-between gap-2 transition-opacity duration-300", stage === "dates" ? "opacity-100" : "opacity-60")}
				style={{ left: L - 2, width: iw + 4, top: T + PLOT_H + 12 }}
			>
				<MonthBox label="From" value={start} onChange={onStart} min={NOW} max={end} disabled={stage !== "dates"} autoFocus={stage === "dates"} />
				<MonthBox label="To" value={end} onChange={onEnd} min={start} disabled={stage !== "dates"} align="right" />
			</div>
		</div>
	);
}

function AxisBox({
	style,
	label,
	below,
	live,
	value,
	onChange,
	unit,
	placeholder,
	autoFocus,
	readOnly,
}: {
	style: React.CSSProperties;
	label: string;
	below?: boolean;
	live: boolean;
	value: string;
	onChange: (v: string) => void;
	unit: string;
	placeholder: string;
	autoFocus?: boolean;
	/** In the last step: go Back to change it, so the dots always match the range. */
	readOnly?: boolean;
}) {
	// On leaving the box, show what was understood in the unit's short form ("2500000" → "$2.5M").
	const tidyText = () => {
		const n = parseAmount(value, unit);
		if (n !== null && fmtInput(n, unit) !== value) onChange(fmtInput(n, unit));
	};
	const caption = <span className="block text-[11px] font-medium text-muted-foreground">{label}</span>;
	return (
		<label className={cn("absolute space-y-0.5 transition-opacity duration-300", live ? "opacity-100" : "opacity-80")} style={style}>
			{!below && caption}
			<Input
				className={cn("h-8 px-2 text-right tabular-nums", live ? "bg-card" : "border-transparent bg-transparent shadow-none")}
				inputMode="decimal"
				value={value}
				placeholder={placeholder}
				onChange={(e) => onChange(e.target.value)}
				onBlur={tidyText}
				autoFocus={autoFocus}
				readOnly={readOnly}
				tabIndex={readOnly ? -1 : undefined}
			/>
			{below && caption}
		</label>
	);
}

function MonthBox({
	label,
	value,
	onChange,
	min,
	max,
	disabled,
	align,
	autoFocus,
}: {
	label: string;
	value: string;
	onChange: (m: string) => void;
	min?: string;
	max?: string;
	disabled?: boolean;
	align?: "right";
	autoFocus?: boolean;
}) {
	return (
		<label className={cn("space-y-0.5", align === "right" && "text-right")}>
			<span className="block text-[11px] font-medium text-muted-foreground">{label}</span>
			<Input
				type="month"
				className="h-8 w-40 px-2"
				value={value}
				min={min}
				max={max}
				disabled={disabled}
				onChange={(e) => e.target.value && onChange(e.target.value)}
				autoFocus={autoFocus}
			/>
		</label>
	);
}

/** A smooth line through the handles that never overshoots them (monotone cubic), in pixels. */
function smooth(p: [number, number][]): string {
	if (p.length < 2) return p.length ? `M${p[0][0]},${p[0][1]}` : "";
	const n = p.length;
	const d = p.slice(0, -1).map(([x0, y0], i) => (p[i + 1][1] - y0) / (p[i + 1][0] - x0 || 1));
	// Flat at a peak or a dip; elsewhere the mean slope, capped at 3× each side's slope so it stays between the handles.
	const m = p.map((_, i) => {
		if (i === 0) return d[0];
		if (i === n - 1) return d[n - 2];
		if (d[i - 1] * d[i] <= 0) return 0;
		const s = (d[i - 1] + d[i]) / 2;
		return Math.sign(s) * Math.min(Math.abs(s), 3 * Math.abs(d[i - 1]), 3 * Math.abs(d[i]));
	});
	let out = `M${p[0][0].toFixed(1)},${p[0][1].toFixed(1)}`;
	for (let i = 0; i < n - 1; i++) {
		const h = (p[i + 1][0] - p[i][0]) / 3;
		out += `C${(p[i][0] + h).toFixed(1)},${(p[i][1] + m[i] * h).toFixed(1)} ${(p[i + 1][0] - h).toFixed(1)},${(p[i + 1][1] - m[i + 1] * h).toFixed(1)} ${p[i + 1][0].toFixed(1)},${p[i + 1][1].toFixed(1)}`;
	}
	return out;
}
